import {
  AlignmentType,
  BorderStyle,
  Bookmark,
  Document,
  Footer,
  Header,
  ImageRun,
  LevelFormat,
  Packer,
  PageNumber,
  PageReference,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  VerticalAlign,
  WidthType,
} from "docx";
import JSZip from "jszip";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import "pdfjs-dist/legacy/build/pdf.worker.entry";
import { viewDocument } from "../utiles/DocumentService";
import { getLabelFromValue } from "../utiles/Dropdown";

/* ------------------------------------------------------------------ */
/* Layout constants – A4                                               */
/* ------------------------------------------------------------------ */
const FONT = "Nirmala UI";
const PAGE_W = 11906;
const PAGE_H = 16838;
const MARGIN = {
  top: 851,
  bottom: 851,
  left: 851,
  right: 567,
  header: 284,
  footer: 340,
};
const CONTENT_W = PAGE_W - MARGIN.left - MARGIN.right;

const SZ = { body: 20, head: 22, title: 26, cover: 36, small: 18 };

const LINE = { style: BorderStyle.SINGLE, size: 4, color: "000000" };
const TABLE_BORDERS = {
  top: LINE,
  bottom: LINE,
  left: LINE,
  right: LINE,
  insideHorizontal: LINE,
  insideVertical: LINE,
};
const NO_LINE = { style: BorderStyle.NONE, size: 0, color: "FFFFFF" };
const NO_BORDERS = {
  top: NO_LINE,
  bottom: NO_LINE,
  left: NO_LINE,
  right: NO_LINE,
  insideHorizontal: NO_LINE,
  insideVertical: NO_LINE,
};
const CELL_MARGINS = { top: 50, bottom: 50, left: 100, right: 100 };

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */
const clean = (v) => (v === undefined || v === null ? "" : String(v).trim());

const joinParts = (parts, sep = ", ") =>
  parts.map(clean).filter(Boolean).join(sep);

const lines = (text) =>
  String(text || "")
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);

const pickText = (ai, manual) =>
  ai && String(ai).trim() !== "" ? ai : manual || "";
const getObservation = (item) => pickText(item.aiObservation, item.observation);
const getSuggestion = (item) => pickText(item.aiSuggestion, item.suggestion);

const isValidDataUrl = (url) =>
  typeof url === "string" && url.startsWith("data:") && url.includes("base64,");

function dataUrlToUint8(dataUrl) {
  if (!isValidDataUrl(dataUrl)) return { bytes: null, mime: "" };
  try {
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    const u8arr = new Uint8Array(bstr.length);
    for (let i = 0; i < bstr.length; i++) u8arr[i] = bstr.charCodeAt(i);
    return { bytes: u8arr, mime };
  } catch {
    return { bytes: null, mime: "" };
  }
}

function imageTypeFromMime(mime) {
  if (/png/i.test(mime)) return "png";
  if (/gif/i.test(mime)) return "gif";
  if (/bmp/i.test(mime)) return "bmp";
  return "jpg";
}

function safeImageRun(dataUrl, width, height) {
  const { bytes, mime } = dataUrlToUint8(dataUrl);
  if (!bytes || !bytes.length) return null;
  return new ImageRun({
    type: imageTypeFromMime(mime),
    data: bytes,
    transformation: { width, height },
  });
}

function getImageSize(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () =>
      resolve({ w: img.naturalWidth || 1, h: img.naturalHeight || 1 });
    img.onerror = () => resolve({ w: 4, h: 3 });
    img.src = dataUrl;
  });
}

async function fitImageRun(dataUrl, maxW = 600, maxH = 820) {
  const { w, h } = await getImageSize(dataUrl);
  const scale = Math.min(maxW / w, maxH / h, 1);
  return safeImageRun(
    dataUrl,
    Math.max(1, Math.round(w * scale)),
    Math.max(1, Math.round(h * scale)),
  );
}

async function fetchAsDataURL(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function pdfToImages(pdfUrl, scale = 1.5) {
  const pdf = await pdfjsLib.getDocument({ url: pdfUrl }).promise;
  const out = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: ctx, viewport }).promise;
    out.push(canvas.toDataURL("image/png"));
  }
  return out;
}

async function markFieldsDirty(blob) {
  try {
    const zip = await JSZip.loadAsync(blob);
    const file = zip.file("word/document.xml");
    if (!file) return blob;
    let xml = await file.async("string");
    xml = xml.replace(
      /<w:fldChar\s+w:fldCharType="begin"(?![^>]*w:dirty)(\s*\/?>)/g,
      '<w:fldChar w:fldCharType="begin" w:dirty="true"$1',
    );
    zip.file("word/document.xml", xml);
    return await zip.generateAsync({
      type: "blob",
      mimeType:
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      compression: "DEFLATE",
    });
  } catch (err) {
    console.error("Could not mark fields dirty:", err);
    return blob;
  }
}

/* ---------------- docx building blocks ---------------- */
function para(
  text,
  {
    bold = false,
    size = SZ.body,
    align = AlignmentType.LEFT,
    italic = false,
    after = 60,
    before = 0,
    pageBreakBefore = false,
  } = {},
) {
  return new Paragraph({
    alignment: align,
    pageBreakBefore,
    spacing: { after, before },
    children: [
      new TextRun({
        text: text ?? "",
        bold,
        size,
        font: FONT,
        italics: italic,
      }),
    ],
  });
}

function bulletPara(text) {
  return new Paragraph({
    numbering: { reference: "bullets", level: 0 },
    alignment: AlignmentType.LEFT,
    spacing: { after: 40 },
    children: [new TextRun({ text, size: SZ.body, font: FONT })],
  });
}

function textParas(text) {
  const ls = lines(text);
  if (!ls.length) return [para("", { after: 120 })];
  return ls.map((l) => para(l, { align: AlignmentType.JUSTIFIED, after: 80 }));
}

function sectionHeading(text, { bookmark, pageBreakBefore = false } = {}) {
  const run = new TextRun({ text, bold: true, size: SZ.head, font: FONT });
  return new Paragraph({
    alignment: AlignmentType.LEFT,
    pageBreakBefore,
    spacing: { before: 240, after: 120 },
    keepNext: true,
    children: bookmark
      ? [new Bookmark({ id: bookmark, children: [run] })]
      : [run],
  });
}

function centered(text, size = SZ.body, bold = true, after = 40) {
  return new Paragraph({
    alignment: AlignmentType.CENTER,
    spacing: { after },
    children: [new TextRun({ text, size, bold, font: FONT })],
  });
}

function cell(children, width, opts = {}) {
  return new TableCell({
    width: { size: width, type: WidthType.DXA },
    margins: CELL_MARGINS,
    verticalAlign: opts.valign || VerticalAlign.TOP,
    columnSpan: opts.columnSpan,
    children: children.length ? children : [para("")],
  });
}

function plainTable(columnWidths, rows) {
  return new Table({
    width: {
      size: columnWidths.reduce((a, b) => a + b, 0),
      type: WidthType.DXA,
    },
    columnWidths,
    borders: TABLE_BORDERS,
    rows,
  });
}

function headerRow(labels, widths) {
  return new TableRow({
    tableHeader: true,
    cantSplit: true,
    children: labels.map((t, i) =>
      cell(
        [para(t, { bold: true, align: AlignmentType.CENTER, after: 0 })],
        widths[i],
        { valign: VerticalAlign.CENTER },
      ),
    ),
  });
}

/* ------------------------------------------------------------------ */
/* Main builder                                                        */
/* ------------------------------------------------------------------ */
async function buildAuditDocx({
  isQuestion = false,
  orgdetails,
  auddetails,
  details,
  annextureList,
  emptypeOptions,
  observationdate,
  user,
  empData,
  scheduleid,
  divcodeList,
  ackContent,
  exSummaryContent,
  introductionContent,
  firstNote,
  PriorityContent,
  lastNote,
  recommendation,
  conclusion,
  references,
  abbreviation,
  documents,
  auditeeCode,
  fetchImagesAsBase64FromServer,
  fetchRespImagesAsBase64FromServer,
  fetchEmployeeImage,
  fetchOrgImage,
}) {
  const orgcode = localStorage.getItem("custCode") || "ptw";
  const envCode = localStorage.getItem("envcode") || "Prod";

  const industry = JSON.parse(auddetails.industry || "{}");
  const occupiers = JSON.parse(auddetails.occupier || "[]");
  const employees = JSON.parse(auddetails.employee || "[]");
  const occ = occupiers[0] || {};
  const employeeTypeMap = Object.fromEntries(
    (emptypeOptions || []).map((o) => [o.value, o.label]),
  );
  const totalMale = employees.reduce((s, e) => s + Number(e.adultMale || 0), 0);
  const totalFemale = employees.reduce(
    (s, e) => s + Number(e.adultFemale || 0),
    0,
  );
  const officer = (empData || []).find((d) => d.code === user) || {};
  const inspectionOfficerName = officer.name || user;
  const inspectionOfficerDesignation = officer.designation || "";

  /* ---------------- Logo ---------------- */
  let logoDataUrl = null;
  if (orgdetails?.logo) {
    if (isValidDataUrl(orgdetails.logo)) {
      logoDataUrl = orgdetails.logo;
    } else {
      try {
        const logoName = orgdetails.logo.split("/").pop();
        const logoBlobUrl = await fetchOrgImage(logoName, orgdetails.code);
        const fetched = await fetchAsDataURL(logoBlobUrl);
        if (isValidDataUrl(fetched)) logoDataUrl = fetched;
      } catch {
        // omit logo
      }
    }
  }

  /* ---------------- Org cover lines (only if present) ---------------- */
  const orgName = clean(orgdetails?.name);
  const orgAddressLine = joinParts([
    orgdetails?.addressline1,
    orgdetails?.city,
    orgdetails?.dist,
    orgdetails?.state,
    orgdetails?.country,
    orgdetails?.pin,
  ]);
  const orgContactLine = joinParts(
    [
      clean(orgdetails?.contact) && `Phone: ${clean(orgdetails?.contact)}`,
      clean(orgdetails?.emailid) && `Email: ${clean(orgdetails?.emailid)}`,
    ],
    "  |  ",
  );

  /* ---------------- Observation images ---------------- */
  const enriched = await Promise.all(
    (details || []).map(async (item) => {
      const [obsImgs] = await Promise.all([
        fetchImagesAsBase64FromServer((item.image || []).join(","), scheduleid),
      ]);
      const obsBase64 = await Promise.all(
        (obsImgs || []).map(async (url) =>
          isValidDataUrl(url)
            ? url
            : await fetchAsDataURL(url).catch(() => null),
        ),
      );
      return { ...item, image: obsBase64.filter(isValidDataUrl) };
    }),
  );

  /* ---------------- Annexures ---------------- */
  const annexImages = [];
  for (const a of annextureList || []) {
    if (!a?.path) continue;
    const filename = a.path.split("/").pop().toLowerCase();
    try {
      const blobUrl = await fetchEmployeeImage(filename, a.value);
      if (filename.endsWith(".pdf")) {
        const pages = await pdfToImages(blobUrl, 1.5);
        pages
          .filter(isValidDataUrl)
          .forEach((p) => annexImages.push({ label: a.label, dataUrl: p }));
      } else {
        const img = await fetchAsDataURL(blobUrl);
        if (isValidDataUrl(img))
          annexImages.push({ label: a.label, dataUrl: img });
      }
    } catch {}
  }

  /* ---------------- Documents ---------------- */
  const docImages = [];
  const groupedDocs = {};
  for (const d of documents || []) {
    const key = Number(d.orderBy) || 0;
    (groupedDocs[key] ||= []).push(d);
  }
  const docOrders = Object.keys(groupedDocs)
    .map(Number)
    .sort((a, b) => a - b);

  for (const order of docOrders) {
    for (const d of groupedDocs[order]) {
      try {
        const response = await viewDocument({
          projectCode: "Ptw",
          envCode,
          folderPath: `${orgcode}/auditee${auditeeCode}`,
          fileName: d.fileName,
        });
        if (!response || response?.cd === 1) continue;

        const ext = d.fileName.split(".").pop().toLowerCase();
        if (["png", "jpg", "jpeg"].includes(ext)) {
          docImages.push({
            order,
            fileName: d.fileName,
            dataUrl: `data:image/${ext};base64,${response}`,
          });
        } else if (ext === "pdf") {
          const pages = await pdfToImages(
            `data:application/pdf;base64,${response}`,
            1.5,
          );
          pages
            .filter(isValidDataUrl)
            .forEach((p) =>
              docImages.push({ order, fileName: d.fileName, dataUrl: p }),
            );
        }
      } catch (err) {
        console.error("Document load error:", d.fileName, err);
      }
    }
  }

  /* ---------------- Sections ---------------- */
  const obsTitle = isQuestion
    ? "QUESTIONS, OBSERVATIONS & SUGGESTIONS"
    : "OBSERVATIONS & SUGGESTIONS";
  const obsIndexLabel = isQuestion
    ? "Questions, Observations & Suggestions"
    : "Observations & Suggestions";

  const sections = [
    {
      no: 1,
      id: "sec_ack",
      index: "Acknowledgement",
      title: "ACKNOWLEDGEMENT",
      present: true,
    },
    {
      no: 2,
      id: "sec_exec",
      index: "Executive Summary",
      title: "EXECUTIVE SUMMARY",
      present: true,
    },
    {
      no: 3,
      id: "sec_intro",
      index: "Introduction",
      title: "INTRODUCTION",
      present: true,
    },
    {
      no: 4,
      id: "sec_method",
      index: "Methodology",
      title: "METHODOLOGY",
      present: true,
    },
    {
      no: 5,
      id: "sec_factory",
      index: "Factory Details",
      title: "FACTORY DETAILS",
      present: true,
    },
    {
      no: 6,
      id: "sec_emp",
      index: "Employees Summary",
      title: "EMPLOYEES SUMMARY",
      present: true,
    },
    {
      no: 7,
      id: "sec_obs",
      index: obsIndexLabel,
      title: obsTitle,
      present: true,
    },
    {
      no: 8,
      id: "sec_priority",
      index: "Risk Priority",
      title: "RISK PRIORITY CATEGORIES",
      present: true,
    },
    {
      no: 9,
      id: "sec_closing",
      index: "Closing Meeting",
      title: "CLOSING MEETING",
      present: true,
    },
    {
      no: 10,
      id: "sec_rec",
      index: "Recommendation",
      title: "RECOMMENDATION",
      present: true,
    },
    {
      no: 11,
      id: "sec_conc",
      index: "Conclusion",
      title: "CONCLUSION",
      present: true,
    },
    {
      no: 12,
      id: "sec_ref",
      index: "References",
      title: "REFERENCES",
      present: true,
    },
    {
      no: 13,
      id: "sec_abb",
      index: "Abbreviation",
      title: "ABBREVIATION",
      present: true,
    },
    {
      no: 14,
      id: "sec_annex",
      index: "Annexure",
      title: "ANNEXURE",
      present: true,
    },
    {
      no: 15,
      id: "sec_doc",
      index: "Documents",
      title: "DOCUMENTS",
      present: true,
    },
  ];
  const sec = Object.fromEntries(sections.map((s) => [s.id, s]));

  let firstContent = true;
  const heading = (id, forcePageBreak = false) => {
    const s = sec[id];
    const pb = forcePageBreak || firstContent;
    firstContent = false;
    return sectionHeading(`${s.no}. ${s.title}`, {
      bookmark: id,
      pageBreakBefore: pb,
    });
  };

  const body = [];

  /* ================= COVER PAGE – org details ================= */
  body.push(
    para("AUDIT OBSERVATION REPORT", {
      bold: true,
      size: SZ.cover,
      align: AlignmentType.CENTER,
      before: 300,
      after: 240,
    }),
  );

  const coverW = [CONTENT_W / 2, CONTENT_W / 2];
  const coverField = (label, value, align = AlignmentType.LEFT) =>
    new Paragraph({
      alignment: align,
      spacing: { after: 80 },
      children: [
        new TextRun({
          text: `${label}: `,
          bold: true,
          size: SZ.body,
          font: FONT,
          color: "C62828",
        }),
        new TextRun({
          text: value || "—",
          bold: true,
          size: SZ.body,
          font: FONT,
        }),
      ],
    });

  body.push(
    new Table({
      width: { size: CONTENT_W, type: WidthType.DXA },
      columnWidths: coverW,
      borders: NO_BORDERS,
      rows: [
        new TableRow({
          children: [
            cell(
              [
                coverField(
                  "Observation Date",
                  observationdate || new Date().toLocaleDateString(),
                ),
              ],
              coverW[0],
            ),
            cell(
              [
                coverField(
                  "Generated By",
                  inspectionOfficerName || "System",
                  AlignmentType.RIGHT,
                ),
              ],
              coverW[1],
            ),
          ],
        }),
        new TableRow({
          children: [
            cell(
              [
                coverField(
                  "Division",
                  getLabelFromValue(auddetails?.divcode, divcodeList),
                ),
              ],
              coverW[0],
            ),
            cell(
              [
                coverField(
                  "Licence No",
                  auddetails?.licencenumber,
                  AlignmentType.RIGHT,
                ),
              ],
              coverW[1],
            ),
          ],
        }),
      ],
    }),
  );

  body.push(new Paragraph({ spacing: { before: 1400 }, children: [] }));

  if (logoDataUrl) {
    const logoRun = await fitImageRun(logoDataUrl, 110, 90);
    if (logoRun) {
      body.push(
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { after: 120 },
          children: [logoRun],
        }),
      );
    }
  }

  // Org name / address / contact – only if present
  if (orgName) {
    body.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 80 },
        children: [
          new TextRun({
            text: orgName,
            bold: true,
            size: SZ.title,
            font: FONT,
            color: "C62828",
          }),
        ],
      }),
    );
  }
  if (orgAddressLine) {
    body.push(centered(orgAddressLine, SZ.body, false));
  }
  if (orgContactLine) {
    body.push(centered(orgContactLine, SZ.body, false));
  }

  /* ================= INDEX ================= */
  body.push(
    para("INDEX", {
      bold: true,
      size: SZ.title,
      align: AlignmentType.CENTER,
      before: 120,
      after: 160,
      pageBreakBefore: true,
    }),
  );

  const idxW = [1200, CONTENT_W - 1200 - 2000, 2000];
  body.push(
    plainTable(idxW, [
      headerRow(["Sl. No.", "Content", "Page"], idxW),
      ...sections.map(
        (s) =>
          new TableRow({
            cantSplit: true,
            children: [
              cell(
                [para(String(s.no), { align: AlignmentType.CENTER, after: 0 })],
                idxW[0],
              ),
              cell([para(s.index, { after: 0 })], idxW[1]),
              cell(
                [
                  new Paragraph({
                    alignment: AlignmentType.CENTER,
                    children: s.present
                      ? [
                          new TextRun({
                            children: [
                              new PageReference(s.id, { hyperlink: true }),
                            ],
                            size: SZ.body,
                            font: FONT,
                          }),
                        ]
                      : [],
                  }),
                ],
                idxW[2],
              ),
            ],
          }),
      ),
    ]),
  );

  /* ================= 1–4 text sections ================= */
  if (sec.sec_ack.present) {
    body.push(heading("sec_ack"));
    body.push(...textParas(ackContent || ""));
  }
  if (sec.sec_exec.present) {
    body.push(heading("sec_exec"));
    body.push(...textParas(exSummaryContent || ""));
  }
  if (sec.sec_intro.present) {
    body.push(heading("sec_intro"));
    body.push(...textParas(introductionContent || ""));
  }
  if (sec.sec_method.present) {
    body.push(heading("sec_method"));
    body.push(...textParas(firstNote || ""));
  }

  /* ================= 5. FACTORY DETAILS ================= */
  const factoryW = [3600, CONTENT_W - 3600];
  const factoryRows = [
    ["Inspection Officer", inspectionOfficerName],
    ["Designation", inspectionOfficerDesignation],
    ["Factory Name", auddetails.name],
    [
      "Factory Address",
      joinParts([auddetails.city, auddetails.dist, auddetails.state]),
    ],
    ["Licence No.", auddetails.licencenumber],
    ["Date of Inspection", observationdate],
    ["Occupier Name", industry.occupieName],
    [
      "Occupier Contact / Email",
      joinParts(
        [industry.occupierPhone, occ.occupierEmail || occ.email],
        " | ",
      ),
    ],
    ["Manager Name", industry.managerName],
    [
      "Manager Contact / Email",
      joinParts([industry.managerPhone, industry.managerEmail], " | "),
    ],
    ["Management Personnel Present", industry.managementper],
  ];

  body.push(heading("sec_factory"));
  body.push(
    plainTable(
      factoryW,
      factoryRows.map(
        ([label, value]) =>
          new TableRow({
            cantSplit: true,
            children: [
              cell([para(label, { bold: true, after: 0 })], factoryW[0]),
              cell([para(clean(value), { after: 0 })], factoryW[1]),
            ],
          }),
      ),
    ),
  );

  /* ================= 6. EMPLOYEES SUMMARY ================= */
  const empW = [CONTENT_W - 2 * 2400, 2400, 2400];
  body.push(heading("sec_emp"));
  body.push(
    plainTable(empW, [
      headerRow(["Employee Type", "Adult Male", "Adult Female"], empW),
      ...employees.map(
        (e) =>
          new TableRow({
            cantSplit: true,
            children: [
              cell(
                [
                  para(
                    employeeTypeMap[e.employeeType] || `Type ${e.employeeType}`,
                    { after: 0 },
                  ),
                ],
                empW[0],
              ),
              cell(
                [
                  para(String(e.adultMale || 0), {
                    align: AlignmentType.CENTER,
                    after: 0,
                  }),
                ],
                empW[1],
              ),
              cell(
                [
                  para(String(e.adultFemale || 0), {
                    align: AlignmentType.CENTER,
                    after: 0,
                  }),
                ],
                empW[2],
              ),
            ],
          }),
      ),
      new TableRow({
        cantSplit: true,
        children: [
          cell([para("Total", { bold: true, after: 0 })], empW[0]),
          cell(
            [
              para(String(totalMale), {
                bold: true,
                align: AlignmentType.CENTER,
                after: 0,
              }),
            ],
            empW[1],
          ),
          cell(
            [
              para(String(totalFemale), {
                bold: true,
                align: AlignmentType.CENTER,
                after: 0,
              }),
            ],
            empW[2],
          ),
        ],
      }),
    ]),
  );

  /* ================= 7. OBSERVATIONS ================= */
  body.push(heading("sec_obs"));

  let counter = 1;

  for (const item of enriched) {
    const obsText = getObservation(item);
    const sugText = getSuggestion(item);

    if (obsText.trim() === "" && sugText.trim() === "") continue;

    // Question mode: question is the heading.
    // Non-question mode: observation itself is the heading.
    const headingText =
      isQuestion && item.question
        ? `${counter}) ${item.question}`
        : `${counter}) ${obsText}`;

    body.push(
      new Paragraph({
        alignment: AlignmentType.LEFT,
        spacing: { before: 180, after: 80 },
        keepNext: true,
        children: [
          new TextRun({
            text: headingText,
            bold: true,
            size: SZ.head,
            font: FONT,
          }),
        ],
      }),
    );

    // Show observation answer only in question mode.
    if (isQuestion && obsText.trim()) {
      lines(obsText).forEach((line) => {
        body.push(bulletPara(line));
      });
    }

    // Always show suggestion directly after the heading/answer.
    if (sugText.trim()) {
      lines(sugText).forEach((line) => {
        body.push(bulletPara(line));
      });
    }

    // Keep observation images unchanged.
    (item.image || [])
      .map((url) => safeImageRun(url, 180, 135))
      .filter(Boolean)
      .forEach((run) =>
        body.push(
          new Paragraph({
            alignment: AlignmentType.LEFT,
            spacing: { before: 60, after: 80 },
            children: [run],
          }),
        ),
      );

    counter++;
  }

  if (counter === 1) {
    body.push(
      para("No observations recorded.", {
        italic: true,
        after: 120,
      }),
    );
  }

  /* ================= 8–13 text sections ================= */
  body.push(heading("sec_priority"));
  body.push(...textParas(PriorityContent));

  body.push(heading("sec_closing"));
  body.push(...textParas(lastNote));

  body.push(heading("sec_rec"));
  body.push(...textParas(recommendation));

  body.push(heading("sec_conc"));
  body.push(...textParas(conclusion));

  body.push(heading("sec_ref"));
  body.push(...textParas(references));

  body.push(heading("sec_abb"));
  body.push(...textParas(abbreviation));

  /* ================= 14. ANNEXURE ================= */
  body.push(heading("sec_annex", true));
  if (!annexImages.length) {
    body.push(para("No annexures attached.", { italic: true }));
  } else {
    let lastLabel = null;
    let annexNo = 0;
    for (const img of annexImages) {
      const run = await fitImageRun(img.dataUrl, 600, 780);
      if (!run) continue;
      if (img.label !== lastLabel) {
        annexNo++;
        lastLabel = img.label;
        body.push(
          para(`Annexure ${annexNo}: ${img.label || "Untitled"}`, {
            bold: true,
            size: SZ.head,
            before: 120,
            after: 80,
            pageBreakBefore: annexNo > 1,
          }),
        );
      } else {
        body.push(new Paragraph({ pageBreakBefore: true, children: [] }));
      }
      body.push(
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { after: 120 },
          children: [run],
        }),
      );
    }
  }

  /* ================= 15. DOCUMENTS ================= */
  body.push(heading("sec_doc", true));
  if (!docImages.length) {
    body.push(para("No documents attached.", { italic: true }));
  } else {
    let first = true;
    for (const d of docImages) {
      const run = await fitImageRun(d.dataUrl, 600, 780);
      if (!run) continue;
      body.push(
        para(`${d.order}. ${d.fileName}`, {
          bold: true,
          before: 120,
          after: 60,
          pageBreakBefore: !first,
        }),
      );
      first = false;
      body.push(
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { after: 120 },
          children: [run],
        }),
      );
    }
  }

  /* ================= Document ================= */
  const doc = new Document({
    features: { updateFields: true },
    styles: {
      default: { document: { run: { font: FONT, size: SZ.body } } },
    },
    numbering: {
      config: [
        {
          reference: "bullets",
          levels: [
            {
              level: 0,
              format: LevelFormat.BULLET,
              text: "\u2022",
              alignment: AlignmentType.LEFT,
              style: { paragraph: { indent: { left: 300, hanging: 220 } } },
            },
          ],
        },
      ],
    },
    sections: [
      {
        properties: {
          page: { size: { width: PAGE_W, height: PAGE_H }, margin: MARGIN },
        },
        headers: {
          default: new Header({
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  new TextRun({
                    children: [PageNumber.CURRENT],
                    size: SZ.body,
                    font: FONT,
                  }),
                ],
              }),
            ],
          }),
        },
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  new TextRun({
                    text: orgdetails?.name || "",
                    bold: true,
                    size: SZ.small,
                    font: FONT,
                  }),
                ],
              }),
            ],
          }),
        },
        children: body,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  return await markFieldsDirty(blob);
}

export default buildAuditDocx;
